import { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, User } from "lucide-react";
import type { MessengerStory } from "@/lib/api";
import {
  dedupeStoryTopicLabels,
  formatStoryDate,
  messengerClassLabel,
  messengerClassStyle,
  messengerPersonaLabel,
} from "@/lib/messengerStoryLabels";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  story: MessengerStory;
  onOpenProfile: (participantId: string) => void;
};

export default function MessengerStoryCard({ story, onOpenProfile }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const topics = dedupeStoryTopicLabels(story);
  const author = story.participant_name?.trim() || "Nieznana osoba";
  const primaryPersona = story.personas?.[0];

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">{author}</h3>
            <time className="text-sm text-slate-500">{formatStoryDate(story.date)}</time>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            <span
              className={cn(
                "inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium",
                messengerClassStyle(story.class_),
              )}
            >
              {messengerClassLabel(story.class_)}
            </span>
            {primaryPersona && (
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                {messengerPersonaLabel(primaryPersona)}
              </span>
            )}
          </div>

          <p className="mt-3 text-base leading-relaxed text-slate-700 line-clamp-3">
            {story.summary}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="gap-1.5"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" />
              Zwiń wiadomość
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" />
              Czytaj wiadomość
            </>
          )}
        </Button>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {story.participant_id && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              onClick={() => onOpenProfile(story.participant_id!)}
            >
              <User className="h-3.5 w-3.5" />
              Profil osoby
            </button>
          )}
          {story.inbox_link && (
            <>
              {story.participant_id && <span className="text-slate-300">·</span>}
              <a
                href={story.inbox_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Inbox Meta
              </a>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
            {story.full_text}
          </div>
          <p className="text-xs text-slate-400">{story.length} znaków</p>

          {topics.length > 0 && (
            <div>
              <button
                type="button"
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
                onClick={() => setShowTopics(!showTopics)}
              >
                {showTopics ? "Ukryj tematy" : `Tematy wiadomości (${topics.length})`}
              </button>
              {showTopics && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {topics.map((label) => (
                    <span
                      key={label}
                      className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
